/**
 * ÖHLINS — klikací rozkresy náhradních dílů.
 *
 * Levý sloupec: vektorové SVG strany rozpisky + overlay klikacích bodů.
 * Pravý sloupec: položky tabulky (pozice, kód, ks, název od výrobce).
 * Obousměrná vazba: najetí na řádek zvýrazní bod ve výkresu a naopak.
 *
 * 🔑🔑 SOUŘADNICE SE NEPŘEKLÁPĚJÍ. KTM `DrawingPage.tsx` dělá Y-flip, protože
 * BFF posílá souřadnice v matematické konvenci (Y = 0 dole). Öhlins body
 * pocházejí z PyMuPDF, které má počátek VLEVO NAHOŘE — stejně jako SVG a CSS.
 * Kdo sem zkopíruje Y-flip z KTM, převrátí výkres vzhůru nohama.
 *
 * ⚠ Jedna pozice může mít víc bodů (symetrické díly mají dva vývody), proto se
 * zvýrazňuje podle `poz`, ne podle indexu bodu.
 */
import { useEffect, useMemo, useState } from "react";
import {
  ohlinsRozkres,
  ohlinsRozkresy,
  ohlinsRozkresyProduktu,
  type OhlinsRozkresDetail,
  type OhlinsRozkresStrana,
} from "../lib/api";

/**
 * Porovnani pozic po ciselnych usecich.
 *
 * POZICE JE TEXT, NE CISLO. Prosty `sort()` da `1-1, 1-10, 1-11 ... 1-19, 1-2`,
 * takze zakaznik hleda devitku az za devatenactkou. Databaze ani API to neresi -
 * kdo si seznam vytahne jinam, musi seradit znovu.
 *
 * Je to PORT `porovnejPoz()` z `apps/ohlins-widget/ohlins.js` (eshopovy widget).
 * Kdyz se jedna zmeni, dorovnat i druhou.
 */
function porovnejPoz(a: string, b: string): number {
  const ca = String(a || "").split(/(\d+)/);
  const cb = String(b || "").split(/(\d+)/);
  for (let i = 0; i < Math.max(ca.length, cb.length); i++) {
    const x = ca[i] || "";
    const y = cb[i] || "";
    if (/^\d+$/.test(x) && /^\d+$/.test(y)) {
      if (Number(x) !== Number(y)) return Number(x) - Number(y);
    } else if (x !== y) {
      return x < y ? -1 : 1;
    }
  }
  return 0;
}

export default function OhlinsPage() {
  const [produkty, setProdukty] = useState<Array<{ produktKod: string; stran: number }>>([]);
  const [celkem, setCelkem] = useState<{ produktu: number; stran: number; bodu: number } | null>(null);
  const [produkt, setProdukt] = useState<string | null>(null);
  const [strany, setStrany] = useState<OhlinsRozkresStrana[]>([]);
  const [detail, setDetail] = useState<OhlinsRozkresDetail | null>(null);
  const [aktivni, setAktivni] = useState<string | null>(null);
  const [chyba, setChyba] = useState<string | null>(null);
  const [nacita, setNacita] = useState(false);

  useEffect(() => {
    ohlinsRozkresy()
      .then((d) => {
        setProdukty(d.produkty);
        setCelkem(d.celkem);
        const prvni = d.produkty[0];
        if (prvni) setProdukt(prvni.produktKod);
      })
      .catch((e) => setChyba(String(e)));
  }, []);

  useEffect(() => {
    if (!produkt) return;
    setDetail(null);
    ohlinsRozkresyProduktu(produkt)
      .then((d) => {
        setStrany(d.strany);
        const prvni = d.strany[0];
        if (prvni) void otevri(prvni.id);
      })
      .catch((e) => setChyba(String(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [produkt]);

  async function otevri(id: string) {
    setNacita(true);
    try {
      setDetail(await ohlinsRozkres(id));
      setAktivni(null);
    } catch (e) {
      setChyba(String(e));
    } finally {
      setNacita(false);
    }
  }

  /** Položky s počtem bodů — co nemá bod, na výkres se nedá kliknout. */
  const polozky = useMemo(() => {
    if (!detail) return [];
    const pocet = new Map<string, number>();
    for (const b of detail.body) pocet.set(b.poz, (pocet.get(b.poz) ?? 0) + 1);
    return detail.polozky
      .map((p) => ({ ...p, bodu: pocet.get(p.poz) ?? 0 }))
      .sort((a, b) => porovnejPoz(a.poz, b.poz));
  }, [detail]);

  if (chyba) {
    return <div className="p-6 text-red-600">Nepodařilo se načíst rozkresy: {chyba}</div>;
  }

  return (
    <div className="p-6 space-y-4">
      <header>
        <h1 className="text-2xl font-semibold">Öhlins — rozkresy</h1>
        {celkem && (
          <p className="text-sm text-zinc-500">
            {celkem.produktu} produktů · {celkem.stran} výkresů · {celkem.bodu} klikacích bodů.
            Pozice jsou vytažené z PDF rozpisek od výrobce.
          </p>
        )}
      </header>

      <div className="flex gap-3 flex-wrap">
        <select
          className="border rounded px-3 py-2 text-sm min-w-56"
          value={produkt ?? ""}
          onChange={(e) => setProdukt(e.target.value)}
        >
          {produkty.map((p) => (
            <option key={p.produktKod} value={p.produktKod}>
              {p.produktKod} ({p.stran} výkresů)
            </option>
          ))}
        </select>

        <div className="flex gap-2 flex-wrap">
          {strany.map((s) => (
            <button
              key={s.id}
              onClick={() => void otevri(s.id)}
              className={`px-3 py-2 text-sm rounded border ${
                detail?.id === s.id ? "bg-orange-500 text-white border-orange-500" : "bg-white"
              }`}
              title={s.nadpis ?? ""}
            >
              {s.nadpis || `Strana ${s.strana}`}
              <span className="opacity-60"> · {s.bodu}</span>
            </button>
          ))}
        </div>
      </div>

      {nacita && <div className="text-sm text-zinc-500">Načítám výkres…</div>}

      {detail && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6 items-start">
          {/* ── výkres ─────────────────────────────────────────────────── */}
          <div className="relative border rounded bg-white overflow-auto" style={{ maxHeight: "78vh" }}>
            <div className="relative inline-block w-full">
              {/* SVG chodí jako text z API — vykreslí se přímo do stránky */}
              <div
                className="block w-full [&>svg]:w-full [&>svg]:h-auto"
                dangerouslySetInnerHTML={{ __html: detail.svg }}
              />
              <svg
                className="absolute inset-0 w-full h-full"
                viewBox="0 0 1 1"
                preserveAspectRatio="none"
              >
                {detail.body.map((b, i) => {
                  const zvyrazneno = aktivni === b.poz;
                  return (
                    <rect
                      key={`${b.poz}-${i}`}
                      x={b.x - 0.006}
                      y={b.y - 0.004}
                      width={b.s + 0.012}
                      height={b.v + 0.008}
                      rx={0.004}
                      fill={zvyrazneno ? "rgba(255,102,0,.38)" : "rgba(255,102,0,.10)"}
                      stroke={zvyrazneno ? "#ff6600" : "rgba(255,102,0,.35)"}
                      strokeWidth={0.0015}
                      style={{ cursor: "pointer" }}
                      onMouseEnter={() => setAktivni(b.poz)}
                      onMouseLeave={() => setAktivni(null)}
                    />
                  );
                })}
              </svg>
            </div>
          </div>

          {/* ── položky ────────────────────────────────────────────────── */}
          <div className="border rounded bg-white">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-left">
                <tr>
                  <th className="px-3 py-2 w-14">Poz.</th>
                  <th className="px-3 py-2">Díl</th>
                  <th className="px-3 py-2 w-10">ks</th>
                </tr>
              </thead>
              <tbody>
                {polozky.map((p) => (
                  <tr
                    key={p.poz}
                    onMouseEnter={() => setAktivni(p.poz)}
                    onMouseLeave={() => setAktivni(null)}
                    className={`border-t cursor-default ${
                      aktivni === p.poz ? "bg-orange-50" : ""
                    }`}
                  >
                    <td className="px-3 py-2 font-mono text-xs">
                      {p.poz}
                      {p.bodu === 0 && (
                        <span className="ml-1 text-zinc-400" title="Ve výkresu nemá bod">
                          ○
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-mono text-xs">{p.dilKod}</div>
                      <div className="text-zinc-600">{p.popis ?? "—"}</div>
                    </td>
                    <td className="px-3 py-2 text-zinc-500">{p.ks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
